import { HttpClient, HttpParams } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  StaffAgreementGenerationRequest,
  StaffAgreementRequest,
  StaffAgreementResponse,
  StaffAgreementSignatureRequest,
  StaffAgreementSignatureResponse,
  StaffAgreementSourceType,
  StaffAgreementStatus,
  StaffAgreementTemplateResponse,
  StaffAgreementTemplateStatus,
  StaffAgreementType,
} from "../model/agreement.model";
import { BASE_PATH } from "../variables";

@Injectable({ providedIn: "root" })
export class StaffAgreementsService {
  private readonly apiUrl: string;

  constructor(
    private readonly http: HttpClient,
    @Inject(BASE_PATH) basePath: string,
  ) {
    this.apiUrl = `${basePath.replace(/\/$/, "")}/api/v1`;
  }

  list(
    filters: {
      type?: StaffAgreementType;
      status?: StaffAgreementStatus;
      sourceType?: StaffAgreementSourceType;
      sourceReferenceId?: number;
    } = {},
  ): Observable<StaffAgreementResponse[]> {
    let params = new HttpParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value != null) params = params.set(key, value);
    });
    return this.http.get<StaffAgreementResponse[]>(
      `${this.apiUrl}/agreements`,
      { params },
    );
  }

  getById(agreementId: number): Observable<StaffAgreementResponse> {
    return this.http.get<StaffAgreementResponse>(
      `${this.apiUrl}/agreements/${agreementId}`,
    );
  }

  create(request: StaffAgreementRequest): Observable<StaffAgreementResponse> {
    return this.http.post<StaffAgreementResponse>(
      `${this.apiUrl}/agreements`,
      request,
    );
  }

  update(
    agreementId: number,
    request: StaffAgreementRequest,
  ): Observable<StaffAgreementResponse> {
    return this.http.put<StaffAgreementResponse>(
      `${this.apiUrl}/agreements/${agreementId}`,
      request,
    );
  }

  generate(
    agreementId: number,
    request: StaffAgreementGenerationRequest,
  ): Observable<StaffAgreementResponse> {
    return this.http.post<StaffAgreementResponse>(
      `${this.apiUrl}/agreements/${agreementId}/generate`,
      request,
    );
  }

  submitForSignature(agreementId: number): Observable<StaffAgreementResponse> {
    return this.http.post<StaffAgreementResponse>(
      `${this.apiUrl}/agreements/${agreementId}/submit-signature`,
      {},
    );
  }

  listSignatures(
    agreementId: number,
  ): Observable<StaffAgreementSignatureResponse[]> {
    return this.http.get<StaffAgreementSignatureResponse[]>(
      `${this.apiUrl}/agreements/${agreementId}/signatures`,
    );
  }

  sign(
    agreementId: number,
    partyId: number,
    request: StaffAgreementSignatureRequest,
  ): Observable<StaffAgreementSignatureResponse> {
    return this.http.post<StaffAgreementSignatureResponse>(
      `${this.apiUrl}/agreements/${agreementId}/parties/${partyId}/sign`,
      request,
    );
  }

  terminate(
    agreementId: number,
    reason: string,
  ): Observable<StaffAgreementResponse> {
    return this.http.post<StaffAgreementResponse>(
      `${this.apiUrl}/agreements/${agreementId}/terminate`,
      { reason },
    );
  }

  cancel(agreementId: number): Observable<StaffAgreementResponse> {
    return this.http.post<StaffAgreementResponse>(
      `${this.apiUrl}/agreements/${agreementId}/cancel`,
      {},
    );
  }

  listTemplates(
    type?: StaffAgreementType,
    status?: StaffAgreementTemplateStatus,
  ): Observable<StaffAgreementTemplateResponse[]> {
    let params = new HttpParams();
    if (type) params = params.set("type", type);
    if (status) params = params.set("status", status);
    return this.http.get<StaffAgreementTemplateResponse[]>(
      `${this.apiUrl}/agreement-templates`,
      { params },
    );
  }
}
